export type LayoutFamily = "dewaal" | "demaas" | "deijssel";
export type LayoutStyle = "rounded" | "leaf";
export type LayoutForm = "single" | "double";

export const newLayoutMinHeight = 2315;
export const newLayoutMaxHeight = 2700;

export type LayoutMeta = {
  family: LayoutFamily;
  style: LayoutStyle;
  form: LayoutForm;
  cut?: boolean;
  conditionalU: boolean;
  roundedHandleLayout: boolean;
  lockHandles?: boolean;
  uHandleLengthWarning?: boolean;
  beamInsetMm?: number;
  twoPanel?: boolean;
};

export const layoutMetaByCode = {
  dewaal: {
    family: "dewaal",
    style: "rounded",
    form: "single",
    conditionalU: false,
    roundedHandleLayout: true,
  },
  dewaal2: {
    family: "dewaal",
    style: "rounded",
    form: "single",
    conditionalU: false,
    roundedHandleLayout: true,
    twoPanel: true,
  },
  dewaaldubbel: {
    family: "dewaal",
    style: "rounded",
    form: "double",
    conditionalU: false,
    roundedHandleLayout: true,
  },
  dewaal2dubbel: {
    family: "dewaal",
    style: "rounded",
    form: "double",
    conditionalU: false,
    roundedHandleLayout: true,
    twoPanel: true,
  },
  dewaal3dubbel: {
    family: "dewaal",
    style: "rounded",
    form: "double",
    conditionalU: false,
    roundedHandleLayout: true,
  },
  dewaal4dubbel: {
    family: "dewaal",
    style: "rounded",
    form: "double",
    conditionalU: false,
    roundedHandleLayout: true,
  },
  dekaapdewaal: {
    family: "dewaal",
    style: "leaf",
    form: "single",
    conditionalU: true,
    roundedHandleLayout: false,
    lockHandles: true,
  },
  debeursdewaal: {
    family: "dewaal",
    style: "leaf",
    form: "single",
    conditionalU: true,
    roundedHandleLayout: false,
    uHandleLengthWarning: true,
    beamInsetMm: 400,
  },
  decooldewaal: {
    family: "dewaal",
    style: "leaf",
    form: "single",
    conditionalU: true,
    roundedHandleLayout: false,
    lockHandles: true,
    beamInsetMm: 300,
  },
  debeursdewaalcut: {
    family: "dewaal",
    style: "leaf",
    form: "single",
    cut: true,
    conditionalU: true,
    roundedHandleLayout: false,
    uHandleLengthWarning: true,
    beamInsetMm: 400,
  },
  dekaapdewaaldubbel: {
    family: "dewaal",
    style: "leaf",
    form: "double",
    conditionalU: true,
    roundedHandleLayout: false,
    lockHandles: true,
  },
  debeursdewaaldubbel: {
    family: "dewaal",
    style: "leaf",
    form: "double",
    conditionalU: true,
    roundedHandleLayout: false,
    uHandleLengthWarning: true,
    beamInsetMm: 400,
  },
  decooldewaaldubbel: {
    family: "dewaal",
    style: "leaf",
    form: "double",
    conditionalU: true,
    roundedHandleLayout: false,
    lockHandles: true,
    beamInsetMm: 300,
  },
  debeursdewaalcutdubbel: {
    family: "dewaal",
    style: "leaf",
    form: "double",
    cut: true,
    conditionalU: true,
    roundedHandleLayout: false,
    uHandleLengthWarning: true,
    beamInsetMm: 400,
  },
  demaas: {
    family: "demaas",
    style: "rounded",
    form: "single",
    conditionalU: false,
    roundedHandleLayout: true,
  },
  demaasdubbel: {
    family: "demaas",
    style: "rounded",
    form: "double",
    conditionalU: false,
    roundedHandleLayout: true,
  },
  demaas2dubbel: {
    family: "demaas",
    style: "rounded",
    form: "double",
    conditionalU: false,
    roundedHandleLayout: true,
  },
  debeursdemaas: {
    family: "demaas",
    style: "leaf",
    form: "single",
    conditionalU: true,
    roundedHandleLayout: false,
    uHandleLengthWarning: true,
    beamInsetMm: 400,
  },
  debeursdemaasdubbel: {
    family: "demaas",
    style: "leaf",
    form: "double",
    conditionalU: true,
    roundedHandleLayout: false,
    uHandleLengthWarning: true,
    beamInsetMm: 400,
  },
  deijssel: {
    family: "deijssel",
    style: "rounded",
    form: "single",
    conditionalU: false,
    roundedHandleLayout: true,
  },
  deijssel1: {
    family: "deijssel",
    style: "rounded",
    form: "single",
    conditionalU: false,
    roundedHandleLayout: true,
  },
  deijsseldubbel: {
    family: "deijssel",
    style: "rounded",
    form: "double",
    conditionalU: false,
    roundedHandleLayout: true,
  },
  deijssel1dubbel: {
    family: "deijssel",
    style: "rounded",
    form: "double",
    conditionalU: false,
    roundedHandleLayout: true,
  },
} as const satisfies Record<string, LayoutMeta>;

export type LayoutCode = keyof typeof layoutMetaByCode;

const orderedDeWaalCodes = [
  "dewaal",
  "dewaal2",
  "dewaaldubbel",
  "dewaal2dubbel",
  "dewaal3dubbel",
  "dewaal4dubbel",
  "dekaapdewaal",
  "debeursdewaal",
  "decooldewaal",
  "debeursdewaalcut",
  "dekaapdewaaldubbel",
  "debeursdewaaldubbel",
  "decooldewaaldubbel",
  "debeursdewaalcutdubbel",
] as const satisfies readonly LayoutCode[];

const orderedDeMaasCodes = [
  "demaas",
  "demaasdubbel",
  "demaas2dubbel",
  "debeursdemaas",
  "debeursdemaasdubbel",
] as const satisfies readonly LayoutCode[];

const orderedDeIjsselCodes = [
  "deijssel",
  "deijssel1",
  "deijsseldubbel",
  "deijssel1dubbel",
] as const satisfies readonly LayoutCode[];

const orderedConditionalUNewLayoutCodes = [
  "debeursdemaas",
  "debeursdewaal",
  "debeursdewaalcut",
  "dekaapdewaal",
  "decooldewaal",
] as const satisfies readonly LayoutCode[];

const orderedDubbelConditionalUNewLayoutCodes = [
  "debeursdemaasdubbel",
  "dekaapdewaaldubbel",
  "debeursdewaaldubbel",
  "decooldewaaldubbel",
  "debeursdewaalcutdubbel",
] as const satisfies readonly LayoutCode[];

const orderedRoundedHandleLayoutCodes = [
  ...orderedDeWaalCodes,
  ...orderedDeIjsselCodes,
  ...orderedDeMaasCodes,
] as const satisfies readonly LayoutCode[];

const getLayoutMeta = (code: string | null | undefined): LayoutMeta | null => {
  if (!code || !(code in layoutMetaByCode)) {
    return null;
  }

  return layoutMetaByCode[code as LayoutCode];
};

const filterOrderedCodes = (
  orderedCodes: readonly LayoutCode[],
  predicate: (meta: LayoutMeta) => boolean,
): string[] => orderedCodes.filter((code) => predicate(layoutMetaByCode[code]));

export const deWaalGroupLayoutCodes = filterOrderedCodes(
  orderedDeWaalCodes,
  (meta) => meta.family === "dewaal" && meta.style === "rounded",
);

export const deWaal2PanelCodes = filterOrderedCodes(
  orderedDeWaalCodes,
  (meta) => meta.family === "dewaal" && !!meta.twoPanel,
);

const deWaalLeafStyleLayoutCodes = filterOrderedCodes(
  orderedDeWaalCodes,
  (meta) => meta.family === "dewaal" && meta.style === "leaf",
);

const deMaasLeafStyleLayoutCodes = filterOrderedCodes(
  orderedDeMaasCodes,
  (meta) => meta.family === "demaas" && meta.style === "leaf",
);

export const deIjsselCodes = [...orderedDeIjsselCodes];

export const lockHandleLayoutCodes = filterOrderedCodes(
  orderedDeWaalCodes,
  (meta) => !!meta.lockHandles,
);

export const uHandleLengthWarningLayoutCodes = [
  ...filterOrderedCodes(
    orderedDeWaalCodes,
    (meta) => !!meta.uHandleLengthWarning,
  ),
  ...filterOrderedCodes(
    orderedDeMaasCodes,
    (meta) => !!meta.uHandleLengthWarning,
  ),
];

export const deWaalCodes = [
  ...deWaalGroupLayoutCodes,
  ...deWaalLeafStyleLayoutCodes,
];

export const deMaasCodes = [
  ...filterOrderedCodes(
    orderedDeMaasCodes,
    (meta) => meta.family === "demaas" && meta.style === "rounded",
  ),
  ...deMaasLeafStyleLayoutCodes,
];

export const conditionalUNewLayoutCodes = filterOrderedCodes(
  orderedConditionalUNewLayoutCodes,
  (meta) => meta.conditionalU && meta.form === "single",
);

export const dubbelConditionalUNewLayoutCodes = filterOrderedCodes(
  orderedDubbelConditionalUNewLayoutCodes,
  (meta) => meta.conditionalU && meta.form === "double",
);

export const roundedHandleLayoutCodes = filterOrderedCodes(
  orderedRoundedHandleLayoutCodes,
  (meta) => meta.roundedHandleLayout,
);

export const isNewLayoutCode = (code: string | null | undefined): boolean =>
  !!getLayoutMeta(code);

export const isDeWaalCode = (code: string | null | undefined): boolean =>
  getLayoutMeta(code)?.family === "dewaal";

export const isDeMaasCode = (code: string | null | undefined): boolean =>
  getLayoutMeta(code)?.family === "demaas";

export const isDeIjsselCode = (code: string | null | undefined): boolean =>
  getLayoutMeta(code)?.family === "deijssel";

export const isConditionalUNewLayoutCode = (
  code: string | null | undefined,
): boolean => !!code && conditionalUNewLayoutCodes.includes(code);

export const isDubbelConditionalUNewLayoutCode = (
  code: string | null | undefined,
): boolean => !!code && dubbelConditionalUNewLayoutCodes.includes(code);

export const isRoundedHandleLayoutCode = (
  code: string | null | undefined,
): boolean => !!code && roundedHandleLayoutCodes.includes(code);

export const isLockHandleLayoutCode = (
  code: string | null | undefined,
): boolean => !!getLayoutMeta(code)?.lockHandles;

/**
 * Layouts where a U-handle stays selectable past the max beam gap; the
 * customer gets a warning instead and the design is adjusted in production.
 */
export const isUHandleLengthWarningLayoutCode = (
  code: string | null | undefined,
): boolean => !!getLayoutMeta(code)?.uHandleLengthWarning;

export const isCutLayoutCode = (code: string | null | undefined): boolean =>
  !!getLayoutMeta(code)?.cut;

export const isDeWaalLeafStyleLayoutCode = (
  code: string | null | undefined,
): boolean => {
  const layoutMeta = getLayoutMeta(code);
  return layoutMeta?.family === "dewaal" && layoutMeta.style === "leaf";
};

export const isDeMaasLeafStyleLayoutCode = (
  code: string | null | undefined,
): boolean => {
  const layoutMeta = getLayoutMeta(code);
  return layoutMeta?.family === "demaas" && layoutMeta.style === "leaf";
};

export const isDeBlaakCode = (code: string | null | undefined): boolean =>
  code === "deblaak";

export const isDeMeentCode = (code: string | null | undefined): boolean =>
  code === "demeent";

export const isEquallySpacedLayoutCode = (
  code: string | null | undefined,
): boolean => isDeBlaakCode(code) || isDeMeentCode(code);

export type ClassicLayoutGroup = "dekaap" | "deblaak" | "demeent";

export const getClassicLayoutGroup = (
  code: string | null | undefined,
): ClassicLayoutGroup | null => {
  if (code === "dekaap") return "dekaap";
  if (isDeBlaakCode(code)) return "deblaak";
  if (isDeMeentCode(code)) return "demeent";
  return null;
};

export type LayoutBrowseGroup =
  | ClassicLayoutGroup
  | "dewaal"
  | "demaas"
  | "deijssel";

export const getLayoutBrowseGroup = (
  code: string | null | undefined,
): LayoutBrowseGroup | null => {
  const classicGroup = getClassicLayoutGroup(code);
  if (classicGroup) return classicGroup;
  if (isDeWaalCode(code)) return "dewaal";
  if (isDeMaasCode(code)) return "demaas";
  if (isDeIjsselCode(code)) return "deijssel";
  return null;
};

export const isSameLayoutBrowseGroup = (
  firstCode: string | null | undefined,
  secondCode: string | null | undefined,
): boolean => {
  const group = getLayoutBrowseGroup(firstCode);
  return group !== null && group === getLayoutBrowseGroup(secondCode);
};

export type VerticalBeamPosition = "left" | "middle" | "right";

export const mirrorVerticalBeamPosition = (
  position: VerticalBeamPosition | undefined,
): VerticalBeamPosition | undefined => {
  if (position === "left") return "right";
  if (position === "right") return "left";
  return position;
};
